import React, { useCallback } from 'react';
import Button from 'carbon-react/lib/components/button';

import { FormattedDate, FormattedMessage, formatCurrency, useIntl } from '@futrli/localisation';

import { usePredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { TransformedContact, useCustomersAndSuppliersContext } from '../../context/customers-and-suppliers-context';
import * as styles from './customer-supplier-card.scss';

interface CustomerSupplierRowProps {
  item: TransformedContact;
}

export const CustomerSupplierRow = ({ item }: CustomerSupplierRowProps) => {
  const intl = useIntl();
  const { currency } = usePredictManagementContext();
  const { handleDeepDive } = useCustomersAndSuppliersContext();

  const handleDeepDiveClick = useCallback(() => {
    handleDeepDive(item.id, item.type);
  }, [handleDeepDive, item.id, item.type]);

  const typeLabel =
    item.type === 'customer'
      ? intl.formatMessage({ id: 'predict.customer', defaultMessage: 'Customer' })
      : intl.formatMessage({ id: 'predict.supplier', defaultMessage: 'Supplier' });

  const currencyCode = currency || '';

  return (
    <div className={styles.row} role="row">
      <div className={styles.nameColumn} role="cell">
        <span className={styles.contactName}>{item.name}</span>
        <span className={styles.contactType}>{typeLabel}</span>
      </div>

      <div className={styles.metricsRow} role="presentation">
        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_total_due" defaultMessage="Total due" />
          </span>
          <span className={styles.metricValue}>{formatCurrency(item.total_amount_due, intl, currencyCode, 0, 0)}</span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_total_overdue" defaultMessage="Total overdue" />
          </span>
          <span className={styles.metricValue}>
            {formatCurrency(item.total_amount_overdue, intl, currencyCode, 0, 0)}
          </span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_total_invoiced" defaultMessage="Total invoiced" />
          </span>
          <span className={styles.metricValue}>
            {formatCurrency(item.total_amount_invoiced, intl, currencyCode, 0, 0)}
          </span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_invoices_due" defaultMessage="Invoices due" />
          </span>
          <span className={styles.metricValue}>{item.unpaid_invoices}</span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_average_sale" defaultMessage="Average sale" />
          </span>
          <span className={styles.metricValue}>{formatCurrency(item.average_sale, intl, currencyCode, 0, 0)}</span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.days_to_pay" defaultMessage="Days to pay" />
          </span>
          <span className={styles.metricValue}>{item.average_days_to_pay}</span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_dependence" defaultMessage="Dependence" />
          </span>
          <span className={styles.metricValue}>
            {item.dependence}
            {'%'}
          </span>
        </div>

        <div className={styles.metric} role="cell">
          <span className={styles.metricLabel}>
            <FormattedMessage id="predict.column_last_activity" defaultMessage="Last activity" />
          </span>
          <span className={styles.metricValue}>
            <FormattedDate value={item.last_activity} month="short" year="2-digit" day="2-digit" />
          </span>
        </div>
      </div>

      <div className={styles.deepDiveColumn} role="cell">
        <Button onClick={handleDeepDiveClick} size="small">
          <FormattedMessage id="predict.deep_dive" defaultMessage="Deep dive" />
        </Button>
      </div>
    </div>
  );
};
